import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Mail } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! You'll get every AI update.");
      setEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
    setLoading(false);
  };

  return (
    <footer className="mt-24">
      {/* Newsletter */}
      <section className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="relative edge-glow bg-[#0A1115]/80 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 80% 0%, rgba(249,115,22,0.18), transparent 55%)" }}
            aria-hidden="true"
          />
          <div className="relative max-w-2xl mx-auto px-6 py-16 md:py-24 text-center">
            <div className="inline-flex items-center gap-2 border border-white/15 bg-white/5 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-orange-300 mb-6">
              <Mail size={12} /> AI Updates — Free
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight text-white leading-tight">
              Every AI update.<br />
              <span className="font-bn-display italic font-normal text-orange-300">Straight to your inbox.</span>
            </h2>
            <p className="mt-5 text-slate-200 max-w-lg mx-auto text-sm md:text-base leading-relaxed">
              New lessons, model launches, and the tools that matter — explained clearly, sent the moment they land.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder:text-slate-200 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/30 transition-colors"
                data-testid="newsletter-email"
              />
              <button type="submit" disabled={loading} className="btn-glow justify-center" data-testid="newsletter-submit">
                {loading ? "..." : <>Subscribe <ArrowRight size={14} /></>}
              </button>
            </form>
            <div className="mt-4 font-bn text-xs text-slate-200">
              No spam — only useful updates. Unsubscribe anytime.{" "}
              <Link to="/newsletter" className="text-orange-300 hover:text-orange-200 underline underline-offset-2 transition-colors" data-testid="newsletter-archive-link">
                See past updates →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer bottom */}
      <div className="mt-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/logo.png?v=3" alt="PromptsHouseBD" className="h-10 w-10 rounded-xl" />
              <span className="font-display font-bold text-white text-lg">
                Prompts<span className="text-gradient">HouseBD</span>
              </span>
            </Link>
            <p className="mt-4 text-sm text-slate-200 max-w-sm leading-relaxed">
              The premium AI learning platform for students, builders, and professionals. Learn practical AI, ship real work.
            </p>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.14em] text-slate-200 font-bold mb-4">Learn</div>
            <ul className="space-y-2.5 text-sm text-slate-200">
              <li><Link to="/lessons" className="hover:text-orange-300 transition-colors">Lessons</Link></li>
              <li><Link to="/news" className="hover:text-orange-300 transition-colors">AI News</Link></li>
              <li><Link to="/prompts" className="hover:text-orange-300 transition-colors">Prompt Library</Link></li>
              <li><Link to="/blog" className="hover:text-orange-300 transition-colors">Blog</Link></li>
              <li><Link to="/newsletter" className="hover:text-orange-300 transition-colors" data-testid="footer-newsletter-link">Newsletter</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.14em] text-slate-200 font-bold mb-4">Follow</div>
            <ul className="space-y-2.5 text-sm text-slate-200">
              <li><a href="#" className="hover:text-orange-300 transition-colors">Facebook</a></li>
              <li><a href="#" className="hover:text-orange-300 transition-colors">YouTube</a></li>
              <li><a href="#" className="hover:text-orange-300 transition-colors">LinkedIn</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-slate-200 flex flex-col md:flex-row justify-between gap-2">
            <div>© 2026 PromptsHouseBD · phousebd.com</div>
            <div>Practical AI, Real Results</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
